<template>
  <div>
    <div v-if="Object.keys(errors).length > 0" class="alert alert-danger">
      <p v-for="error in errors" :key="error" class="errors">
        {{ error }}
      </p>
    </div>
    <div :class="{'has-error':errors.variable}">
      <select :value="variable" name="variable" class="form-control" @change="handleChange">
        <option value="" selected="selected">
          Select a Variable
        </option>
        <optgroup v-for="(variables, type) in filteredStudyVariablesByType" :key="type" :label="getTypeDisplay(type)">
          <option v-for="($variable, $id) in variables" :key="$id" :value="$id">
            {{ $variable.name }}
          </option>
        </optgroup>
      </select>
    </div>
    <div v-if="variable">
      <div v-if="!showComparatorChoices">
        <span :class="{'has-error':errors.operator}">
          <select
            :value="operator"
            name="operator"
            class="form-control selectpicker"
            @input="handleChange"
          >
            <option value="" selected="selected">Select Operator</option>
            <optgroup label="All">
              <option v-for="(label, value) in unaryOperatorLabelsForVariable" :key="value" :value="value">{{ label }}</option>
            </optgroup>
            <optgroup v-if="showNumericOperators" label="Numeric">
              <option v-for="(label, value) in numericOperatorLabels" :key="value" :value="value">{{ label }}</option>
            </optgroup>
            <optgroup v-if="showTextOperators" label="Text">
              <option v-for="(label, value) in textOperatorLabels" :key="value" :value="value">{{ label }}</option>
            </optgroup>
            <optgroup v-if="showDateOperators" label="Date">
              <option v-for="(label, value) in dateOperatorLabels" :key="value" :value="value">{{ label }}</option>
            </optgroup>
          </select>
        </span>
        <span v-if="operator && operatorHasComparator">
          <select
            :value="comparator_type"
            name="comparator_type"
            class="form-control"
            @change="handleChange"
          >
            <option v-if="operatorIsNumeric" value="amount">Exact Amount</option>
            <option v-if="operatorIsText" value="text">Text Input</option>
            <option value="variable">Variable Value</option>
          </select>
          <span v-if="comparator_type === 'amount'" :class="{'has-error':errors.comparator}">
            <input
              :value="comparator"
              name="comparator"
              class="form-control input-numeric"
              @input="handleChange"
            >
          </span>
          <span v-else-if="comparator_type === 'text'" :class="{'has-error':errors.comparator}">
            <input
              :value="comparator"
              name="comparator"
              class="form-control"
              placeholder="Text Input"
              @input="handleChange"
            >
          </span>
          <span v-else-if="comparator_type === 'variable'" :class="{'has-error':errors.comparator}">
            <select :value="comparator" name="comparator" class="form-control" @change="handleChange">
              <option value="" selected="selected">Select a variable</option>
              <optgroup v-for="(variables, type) in variableArgumentOptions" :key="type" :label="getTypeDisplay(type)">
                <option v-for="($variable, $id) in variables" :key="$id" :value="$id">
                  {{ $variable.name }}
                </option>
              </optgroup>
            </select>
          </span>
        </span>
      </div>
    </div>
    <div v-if="hasComparatorChoiceList" :class="{'has-error':errors.comparator_choices}">
      is one of
      <div v-for="(value, key) in comparatorChoiceList" :key="key">
        <input
          v-model="selectedComparatorChoices"
          :value="value.name"
          type="checkbox"
          name="comparator_choices"
        >
        <label>{{ value.name }}</label>
      </div>
    </div>
  </div>
</template>
<script>
  import BaseComponent from '@/EventLogic/components/BaseComponent.vue';
  import {
    operatorIsNumeric,
    operatorIsText,
    operatorIsUnary,
    numericOperatorLabels,
    textOperatorLabels,
    dateOperatorLabels,
    daysOfWeek,
    getComparatorType,
    getUnaryOperatorLabels
  } from "@/EventLogic/operators";

  export default {
    extends: BaseComponent,

    props: {
      variable: [Number, String],
      comparator_type: {
        type: String,
        default: 'amount'
      },
      comparator: String,
      operator: String,
      comparator_choices: {
        type: Array,
        default: () => []
      },
      id: String,
    },
    data: function () {
      return {
        numericOperatorLabels,
        textOperatorLabels,
        dateOperatorLabels,
        daysOfWeek,
        selectedComparatorChoices: this.comparator_choices,
      }
    },
    computed: {
      unaryOperatorLabelsForVariable: function() {
        // todo: For variables that have operators, which variable types can't be an empty string (date)? Which can't be null?
        return getUnaryOperatorLabels(true, true);
      },
      hasComparatorChoiceList: function(){
        return this.operator === 'dateIsOneOf' || this.showComparatorChoices;
      },
      comparatorChoiceList: function(){
        return this.operator === 'dateIsOneOf' ? this.daysOfWeek : this.config.studyVariables[this.variable].options;
      },
      /** Variables that are options for 'Participant variable value'
       *
       * todo: why is it called filtered if no filtering is happening? refactor this to be called variablesMappedByType
       */
      filteredStudyVariablesByType: function () {
        return this.cloneVariableList(false);
      },
      /** Variables to include as argument options */
      variableArgumentOptions: function () {
        let includeList = false;
        // todo: filtering for other types?
        if(this.showDateOperators) {
          // todo: set is_date on ParticipantDataVariable and filter here
          includeList = ['DateVariable', 'ParticipantDataVariable'];
        }
        return this.cloneVariableList(includeList);
      },
      showNumericOperators: function() {
        return Boolean(this.config.studyVariables[this.variable]?.is_numeric);
      },
      showTextOperators: function() {
        return Boolean(this.config.studyVariables[this.variable]?.is_text);
      },
      showDateOperators: function() {
        return Boolean(this.config.studyVariables[this.variable]?.is_date);
      },
      showComparatorChoices: function() {
        return Boolean(this.config.studyVariables[this.variable]?.is_multiple_choice)
      },
      operatorIsNumeric: function() {
        return operatorIsNumeric(this.operator);
      },
      operatorIsText: function() {
        return operatorIsText(this.operator);
      },
      operatorHasComparator: function() {
        if (this.hasComparatorChoiceList) {
          return false;
        }
        return !operatorIsUnary(this.operator);
      }
    },
    watch: {
      'variable': function() {
        this.$emit('update:operator', ''); // Updates comparator_type to '' in operator watch
        this.selectedComparatorChoices = []; // Updates comparator_choices to [] in selectedComparatorChoices watch
      },
      'operator': function() {
        this.selectedComparatorChoices = []; // Updates comparator_choices to [] in selectedComparatorChoices watch
        this.$emit('update:comparator_type', getComparatorType(this.operator));
      },
      'comparator_type': function() {
        this.$emit('update:comparator', undefined);
      },
      showComparatorChoices: function (val) {
        if (val) {
          this.$emit('update:comparator_type', 'multiple_choice');
          if (this.showDateOperators) {
            this.$emit('update:operator', 'dateIsOneOf');
          } else {
            this.$emit('update:operator', 'isOneOf');
          }
        }
      },
      /** Emits to update prop with value from data */
      selectedComparatorChoices: function() {
        this.$emit('update:comparator_choices', this.selectedComparatorChoices);
      },
      comparator_choices: function(value) {
        this.selectedComparatorChoices = value;
      },
    },
    methods: {
      getTypeDisplay: function(type) {
        return this.config.studyVariableTypeDisplayMap[type];
      },
      /** @param categoriesToInclude array of category names as strings, if omitting any categories, or false to include all */
      cloneVariableList(categoriesToInclude = false) {
        const studyVariablesByTypeCopy = {};
        for (const key in this.config.studyVariablesByType) {
          if(categoriesToInclude === false || categoriesToInclude.includes(key)) {
            studyVariablesByTypeCopy[key] = this.config.studyVariablesByType[key];
          }
        }
        return studyVariablesByTypeCopy;
      },
      validate: function() {
        this.errors = {};
        if (!this.variable) {
          this.errors.variable = 'Please select a Variable.';
        } else {
          if (!this.operator) {
            this.errors.operator = "Please select a comparison operator.";
          }

          if (this.hasComparatorChoiceList && this.comparator_choices.length < 1) {
              this.errors.comparator_choices = "Please select at least one valid choice.";
          }

          if (this.operatorHasComparator && !this.comparator) {
              this.errors.comparator = "Please select something to compare against.";
          }
        }

        return Object.keys(this.errors).length === 0;
      }
    }
  };
</script>
